import { useRouter } from "next/navigation";
import { useMemo, type JSX } from "react";
import { useSelector } from "react-redux";
import { Divider } from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import ExpandTitle from "@/components/atoms/expander/expand-title";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { RootState } from "@/store/root-reducer";

interface Props {
  brand: "staysure" | "avanti";
  travellerTitle?: string | null;
  travellersLabel?: string | null;
  partyTypeLabel?: string | null;
  addBtnLabel?: string | null;
  editBtnLabel?: string | null;
  partyTypeBtnLabel?: string | null;
  isOpen: boolean;
  onToggle: () => void;
}
function EditTavellers({
  travellerTitle = "View or edit travellers",
  travellersLabel = "Travellers",
  partyTypeLabel = "Party type",
  addBtnLabel = "Add/edit traveller",
  editBtnLabel = "Edit traveler",
  partyTypeBtnLabel = "Edit party type",
  isOpen,
  onToggle,
  brand,
}: Readonly<Props>): JSX.Element {
  const router = useRouter();
  const { current } = useWizard();
  const { t } = useWizardTranslation();
  const { attributes } = useSelector((state: RootState) => state.quote);
  const tripType = attributes.tripDetails?.partyType;
  const travellers = attributes.travellerDetails?.traveller;
  const btnLabel = travellers?.length === 9 ? editBtnLabel : addBtnLabel;

  const trip = useMemo(() => {
    if (!tripType) return "";

    const tripMapping = {
      OTHER: t("group", "Group"),
      FAMILY: t("family", "Family"),
      COUPLE: t("couple", "Couple"),
      INDIVIDUAL: t("individual", "Individual"),
      SINGLE_PARENT_FAMILY: t("family", "Family"),
    };
    const group = tripType
      ? tripMapping[tripType as keyof typeof tripMapping]
      : "";
    return group;
  }, [tripType, t]);

  const renderBtn = (onClickMethod: () => void, lable: string) => {
    return (
      <div className="flex">
        <LabelButtonWrapper
          hierarchy="tertiary"
          label={lable}
          className="text-[16px] font-sub-section-header-font font-[600]
          !px-0 leading-[16px] h-[17px]"
          onClick={onClickMethod}
        />
      </div>
    );
  };
  const handleAddBtn = () => {
    router.push(current.edges.travellersEdit ?? "");
  };
  const handlePartyType = () => {
    router.push(current.edges.travellersEdit ?? "");
  };

  return (
    <div data-testid="edit-travellers">
      <ExpandTitle
        title={travellerTitle}
        setIsShow={onToggle}
        initShow={isOpen}
        id="edit-quote-travellers-toggle"
        brand={brand}
      />
      {isOpen && (
        <div className="flex flex-col items-start gap-spacing-s">
          <div className="w-full">
            <div className="flex flex-col ">
              <span className="text-functional-subtle">{travellersLabel}</span>
              {travellers?.map((traveller, index) => (
                <div key={traveller.encryptedId}>
                  <p className="font-semibold text-surface-neutral-option-10 font-sub-section-header-font">
                    {traveller.firstName} {traveller.lastName}, {traveller.age}
                  </p>
                  {index < travellers.length - 1 && (
                    <Divider
                      thickness={1}
                      className="!my-spacing-s"
                      brand={brand}
                    />
                  )}
                </div>
              ))}
              <div
                className="flex flex-row justify-between pt-spacing-l sm:pb-spacing-l md:pb-spacing-xl font-semibold"
                id="edit-quote-travellers"
              >
                {renderBtn(handleAddBtn, btnLabel ?? "")}
              </div>
              <Divider thickness={1} className="!mb-spacing-l" brand={brand} />
              <div className="mb-spacing-l">
                <p className="text-functional-subtle font-sub-section-header-font">
                  {partyTypeLabel}
                </p>
                <p className="font-semibold text-surface-neutral-option-10 font-sub-section-header-font">
                  {trip}
                </p>
              </div>
              <div
                className="flex flex-row justify-between sm:mb-spacing-s md:mb-spacing-m font-semibold"
                id="edit-quote-party-type"
              >
                {renderBtn(handlePartyType, partyTypeBtnLabel ?? "")}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default EditTavellers;
